import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../../data";
import { useAccount, type StaffWorkspace } from "../../../app/session";
import { PillView, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { date, time } from "../../../lib/format";
import { currentLang, t } from "../../../lib/sprache";
import { insightsVorbereitungText, useInsightsBereit } from "../../../components/Tarif";
import { finishIgConnect, requestIgSync, startIgConnect } from "../../creator/actions";
import { igCallbackReason, igSyncStateText } from "../../creator/instagram-status";
import { arbeitsbereiche } from "../../../app/bereichswahl-model";
import { setIgEnabled } from "./data";
import { merkeIgRuecksprung } from "../../../app/ig-ruecksprung";
import { useEntitlements, useIg } from "./hooks";
import { formatCount, igStatusPill, pill, type TalentProfile } from "./model";
import { ConfirmAction, TarifWaehlen } from "./parts";

/** Instagram-Anmeldung ohne Meta-App (Test-System, neue Installation) */
export const igNichtEingerichtet = () => t("Die Instagram-Anmeldung ist hier noch nicht eingerichtet. Bitte später noch einmal versuchen.", "The Instagram login is not set up here yet. Please try again later.");

/**
 * Instagram im eigenen Creator-Konto (S2, 0540): Bisher ließ sich Instagram nur im Creator-Portal einer Agentur verbinden. Hier
 * verbindet die Person ihr eigenes Profil selbst, sieht Stand und Kennzahlen, aktualisiert und trennt. Dasselbe Instagram-Konto
 * darf zusätzlich im Creator-Portal einer Agentur verbunden sein; beide Verbindungen sind getrennt (eigener Schlüssel, eigene Werte).
 */
export function StudioInstagram({ ws, p, planName }: { ws: StaffWorkspace; p: TalentProfile; planName: string | null }) {
  const tl = p.talent;
  const ig = useIg(ws.org.org_id, tl.id);
  const ent = useEntitlements(ws.org.org_id);
  const insightsBereit = useInsightsBereit();
  const { account: konto } = useAccount();
  const auchBeiAgentur = konto ? arbeitsbereiche(konto).some((b) => b.art === "agentur") : false;
  const moduleOff = ent.data ? (ent.data.limits.module_insights ?? 0) < 1 : false;
  const { busy, error, run } = useAction();
  const [params, setParams] = useSearchParams();
  const [hinweis, setHinweis] = useState<string | null>(null);
  const erledigt = useRef(false);
  const account = ig.data?.account ?? null;
  const lang = currentLang();

  // Rückkehr von Instagram (über /creator/instagram hierher weitergeleitet): Ticket einlösen, Abbruch oder Fehler als Satz
  const ticket = params.get("ticket");
  const igParam = params.get("ig");
  const grund = params.get("grund");
  useEffect(() => {
    if (erledigt.current || (!ticket && igParam !== "abgebrochen" && igParam !== "fehler")) return;
    erledigt.current = true;
    const aufraeumen = () => {
      const next = new URLSearchParams(params);
      for (const k of ["ticket", "ig", "grund"]) next.delete(k);
      setParams(next, { replace: true });
    };
    if (ticket) {
      void run("verbinden", () => finishIgConnect(api, ticket), t("Instagram ist verbunden. Der erste Abgleich läuft.", "Instagram is connected. The first sync is running.")).finally(aufraeumen);
    } else if (igParam === "abgebrochen") {
      setHinweis(t("Du hast die Anmeldung bei Instagram abgebrochen. Es wurde nichts verbunden.", "You cancelled the Instagram login. Nothing was connected."));
      aufraeumen();
    } else {
      setHinweis(igCallbackReason(grund, lang));
      aufraeumen();
    }
  }, [ticket, igParam, grund, params, setParams, run, lang]);

  async function verbinden() {
    setHinweis(null);
    if (!tl.ig_enabled) await setIgEnabled(api, tl.id, true);
    // Praxistest 04.11.2026: noch nicht eingerichtet (keine Meta-App, Hinweis instagram:not_configured der Serverfunktion) ist ein
    // Zustand, kein Fehler: der Satz steht einmal in der Karte, nicht zusätzlich als Fehler und Toast aus useAction
    const url = await startIgConnect(api, tl.id).catch((e: unknown) => {
      if ((e as { hint?: string } | null)?.hint === "instagram:not_configured") return null;
      throw e;
    });
    if (!url) {
      setHinweis(igNichtEingerichtet());
      return;
    }
    merkeIgRuecksprung(`/agentur/creator/${tl.id}`);
    window.location.assign(url);
  }
  async function aktualisieren() {
    const r = await requestIgSync(api, tl.id);
    toast(igSyncStateText(r.state, r.retry_after, lang));
  }
  const zeitpunkt = (iso: string) => `${date(iso, ws.org.timezone)}, ${time(iso, ws.org.timezone)}`;
  const archived = tl.status === "archived";

  return (
    <section className="panel stack tk-ig-verbindung" aria-labelledby="studio-ig-titel">
      <div className="row between">
        <h3 id="studio-ig-titel">Instagram</h3>
        <PillView pill={!insightsBereit && !account ? pill(t("In Vorbereitung", "In preparation"), "neutral") : igStatusPill(account)} />
      </div>
      {ig.isPending && <p className="muted">{t("Wird geladen …", "Loading …")}</p>}
      {ig.error && <p className="tk-error" role="alert">{t("Die Instagram-Daten konnten nicht geladen werden.", "The Instagram data could not be loaded.")}</p>}
      {!insightsBereit ? <p>{insightsVorbereitungText()}</p>
        : moduleOff ? <p>{t("Instagram-Insights sind im Tarif ", "Instagram insights are not included in the plan ")}{planName ?? ""}{t(" nicht enthalten.", ".")} <TarifWaehlen /></p>
        : account ? (
          <>
            <p>
              <strong>@{account.username}</strong>{" · "}
              {account.last_sync_at ? t(`Stand ${zeitpunkt(account.last_sync_at)}`, `As of ${zeitpunkt(account.last_sync_at)}`) : t("noch kein Abgleich", "no sync yet")}
              {" · "}{t(`${formatCount(account.followers_count)} Follower`, `${formatCount(account.followers_count)} followers`)}
            </p>
            {account.status === "expired" && <p>{t("Die Anmeldung bei Instagram ist abgelaufen. Verbinde dein Konto neu.", "The Instagram sign-in has expired. Reconnect your account.")}</p>}
            {account.last_error && <p className="tk-error">{t("Letzter Fehler beim Abgleich: ", "Last sync error: ")}{account.last_error}</p>}
          </>
        ) : !ig.isPending && <p>{t("Verbinde dein Instagram-Konto, um Reichweite, Aufrufe und Interaktionen hier zu sehen.", "Connect your Instagram account to see reach, views and interactions here.")}</p>}
      {auchBeiAgentur && (
        <p className="muted">{t("Dasselbe Instagram-Konto kann zusätzlich im Creator-Portal deiner Agentur verbunden sein. Beide Verbindungen sind getrennt: Die Agentur sieht nur ihre eigene.",
          "The same Instagram account can also be connected in your agency's creator portal. Both connections are separate: the agency only sees its own.")}</p>
      )}
      {hinweis && <p role="status">{hinweis}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      {insightsBereit && !moduleOff && !archived && (
        <div className="row">
          {(!account || account.status !== "active") && (
            <button type="button" className={account ? "" : "primary"} disabled={!!busy} aria-busy={busy === "start"}
              onClick={() => void run("start", verbinden)}>
              {busy === "start" ? t("Wird geöffnet …", "Opening …") : account ? t("Instagram neu verbinden", "Reconnect Instagram") : t("Mit Instagram verbinden", "Connect with Instagram")}
            </button>
          )}
          {account?.status === "active" && (
            <button type="button" disabled={!!busy} aria-busy={busy === "sync"} onClick={() => void run("sync", aktualisieren)}>
              {busy === "sync" ? t("Wird angefragt …", "Requesting …") : t("Aktualisieren", "Refresh")}
            </button>
          )}
          {account && (
            <ConfirmAction label={t("Instagram trennen", "Disconnect Instagram")} danger busy={busy === "trennen"}
              question={t("Instagram trennen? Verbindung, Schlüssel und Werte in deinem Konto werden gelöscht. Eine Verbindung im Creator-Portal einer Agentur bleibt bestehen.",
                "Disconnect Instagram? The connection, key and values in your account are deleted. A connection in an agency's creator portal stays in place.")}
              confirmLabel={t("Trennen", "Disconnect")}
              onConfirm={() => run("trennen", () => api.rpc("ig_disconnect", { p_talent: tl.id }), t("Instagram ist getrennt.", "Instagram is disconnected."))} />
          )}
        </div>
      )}
    </section>
  );
}
